/**
 * Declared-metrics editor (ADR-0029) — a section on the Semantica page.
 *
 * A metric is the BI agent's "CLAUDE.md" for a dataset: declared once (name +
 * aggregation over a field + optional always-AND filter + suggested dimensions),
 * Ask / the app assistant read it as ground truth instead of re-inferring it. This
 * is the operator's authoring surface; the agent reads what's saved here.
 *
 * Operator console — native selects are acceptable (like DatasetFilterDialog).
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, Pencil, Plus, Sigma, Sparkles, Trash2 } from "lucide-react";

import {
  ConditionRows,
  rowsIncomplete,
  rowsToSpec,
  specToRows,
  type ConditionRow,
} from "@/components/datasets/conditionBuilder";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  datasetMetricsApi,
  type DatasetMetric,
  type DatasetMetricInput,
} from "@/lib/api-dataset-metrics";
import { FeatureTip } from "@/components/billing/FeatureGate";

const selectClass =
  "w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

/** A compact "sum(importo) · dove … · per cliente" summary for the list row.
 * Accepts both a saved metric and an unsaved draft (the propose shape). */
export function metricSummary(
  m: Pick<DatasetMetric, "aggregation_fn"> & Partial<Pick<DatasetMetric, "field" | "filter" | "default_dimensions">>,
  dove: string,
  per: string,
): string {
  const head = m.field ? `${m.aggregation_fn}(${m.field})` : `${m.aggregation_fn}(*)`;
  const bits = [head];
  if (m.filter?.conditions?.length) {
    bits.push(`${dove} ${m.filter.conditions.map((c) => c.field).join(", ")}`);
  }
  if (m.default_dimensions?.length) {
    bits.push(`${per} ${m.default_dimensions.join(", ")}`);
  }
  return bits.join(" · ");
}

export function MetricsSection({
  datasetId,
  fields,
  aggregations,
  canWrite,
}: {
  datasetId: string;
  fields: { name: string; type?: string | null }[];
  aggregations: string[];
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<DatasetMetric | "new" | null>(null);
  const [proposing, setProposing] = React.useState(false);

  const metricsQ = useQuery({
    queryKey: ["datasets", datasetId, "metrics"],
    queryFn: () => datasetMetricsApi.list(datasetId),
  });
  const metrics = metricsQ.data ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => datasetMetricsApi.remove(datasetId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["datasets", datasetId, "metrics"] }),
  });

  return (
    <section className="rounded-lg border border-border/60 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sigma className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold">
            {t("metrics.title", "Metriche dichiarate")}
          </h3>
          {metrics.length > 0 && (
            <span className="text-xs text-muted-foreground">({metrics.length})</span>
          )}
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <FeatureTip feature="ai_plane">
              <Button size="sm" variant="ghost" onClick={() => setProposing(true)}>
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {t("metrics.propose", "Proponi metriche")}
              </Button>
            </FeatureTip>
            <Button size="sm" variant="outline" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("metrics.add", "Aggiungi metrica")}
            </Button>
          </div>
        )}
      </div>

      <p className="mb-3 text-xs text-muted-foreground">
        {t(
          "metrics.help",
          "Definite una volta una metrica (es. fatturato = somma di importo) e l'assistente la userà come verità, invece di reinventarla a ogni domanda.",
        )}
      </p>

      {metrics.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {t("metrics.none", "Nessuna metrica dichiarata.")}
        </p>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>{t("metrics.name", "Nome")}</Th>
              <Th>{t("metrics.definition", "Definizione")}</Th>
              {canWrite && <Th className="w-20" />}
            </Tr>
          </Thead>
          <Tbody>
            {metrics.map((m) => (
              <Tr key={m.id}>
                <Td className="font-medium">
                  {m.name}
                  {m.description && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {m.description}
                    </span>
                  )}
                </Td>
                <Td className="font-mono text-xs text-muted-foreground">
                  {metricSummary(
                    m,
                    t("metrics.where", "dove"),
                    t("metrics.per", "per"),
                  )}
                </Td>
                {canWrite && (
                  <Td>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("common.edit", "Modifica")}
                        onClick={() => setEditing(m)}
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("common.delete", "Elimina")}
                        onClick={() => {
                          if (window.confirm(t("metrics.confirmDelete", "Eliminare la metrica «{{name}}»?", { name: m.name }))) {
                            remove.mutate(m.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {editing && (
        <MetricDialog
          datasetId={datasetId}
          fields={fields}
          aggregations={aggregations}
          metric={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {proposing && (
        <ProposeMetricsDialog datasetId={datasetId} onClose={() => setProposing(false)} />
      )}
    </section>
  );
}

function ProposeMetricsDialog({
  datasetId,
  onClose,
}: {
  datasetId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [picked, setPicked] = React.useState<Set<number>>(new Set());

  // Fires the POST once on open (review-only — nothing is saved server-side).
  const proposeQ = useQuery({
    queryKey: ["datasets", datasetId, "metrics", "propose"],
    queryFn: () => datasetMetricsApi.propose(datasetId),
    retry: false,
    staleTime: 0,
  });
  const drafts = proposeQ.data ?? [];

  React.useEffect(() => {
    if (proposeQ.data) setPicked(new Set(proposeQ.data.map((_, i) => i))); // default: all
  }, [proposeQ.data]);

  const save = useMutation({
    mutationFn: async () => {
      for (let i = 0; i < drafts.length; i++) {
        if (picked.has(i)) await datasetMetricsApi.create(datasetId, drafts[i]);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["datasets", datasetId, "metrics"] });
      onClose();
    },
  });

  const toggle = (i: number) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("metrics.proposeTitle", "Metriche proposte dall'AI")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {proposeQ.isLoading && (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              {t("metrics.proposing", "Odino sta proponendo le metriche…")}
            </p>
          )}
          {proposeQ.isError && (
            <Alert tone="danger" title={t("metrics.proposeFailed", "Proposta non disponibile")}>
              <span className="font-mono text-xs">
                {/ai_unconfigured/.test((proposeQ.error as Error).message)
                  ? t("metrics.proposeUnconfigured", "Nessun provider AI configurato.")
                  : (proposeQ.error as Error).message}
              </span>
            </Alert>
          )}
          {proposeQ.isSuccess && drafts.length === 0 && (
            <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
              {t("metrics.proposeNone", "Nessuna metrica proposta per questo dataset.")}
            </p>
          )}
          {drafts.map((d, i) => (
            <label
              key={i}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-sm"
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={picked.has(i)}
                onChange={() => toggle(i)}
              />
              <span className="min-w-0">
                <span className="font-medium">{d.name}</span>
                <span className="block font-mono text-xs text-muted-foreground">
                  {metricSummary(d, t("metrics.where", "dove"), t("metrics.per", "per"))}
                </span>
                {d.description && (
                  <span className="block text-xs text-muted-foreground">{d.description}</span>
                )}
              </span>
            </label>
          ))}
          {save.isError && (
            <Alert tone="danger" title={t("metrics.saveFailed", "Impossibile salvare")}>
              <span className="font-mono text-xs">{(save.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button
            disabled={save.isPending || picked.size === 0}
            onClick={() => save.mutate()}
          >
            {t("metrics.saveSelected", "Salva selezionate")} ({picked.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetricDialog({
  datasetId,
  fields,
  aggregations,
  metric,
  onClose,
}: {
  datasetId: string;
  fields: { name: string; type?: string | null }[];
  aggregations: string[];
  metric: DatasetMetric | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [name, setName] = React.useState(metric?.name ?? "");
  const [agg, setAgg] = React.useState(metric?.aggregation_fn ?? aggregations[0] ?? "count");
  const [field, setField] = React.useState(metric?.field ?? "");
  const [description, setDescription] = React.useState(metric?.description ?? "");
  const [dims, setDims] = React.useState<string[]>(metric?.default_dimensions ?? []);
  const [match, setMatch] = React.useState<"all" | "any">(metric?.filter?.match ?? "all");
  const [rows, setRows] = React.useState<ConditionRow[]>(() => specToRows(metric?.filter));

  const isCount = agg === "count"; // count(*) — no field
  const needsField = !isCount;

  const save = useMutation({
    mutationFn: () => {
      const body: DatasetMetricInput = {
        name: name.trim(),
        description: description.trim() || null,
        aggregation_fn: agg,
        field: isCount ? null : field || null,
        filter: rowsToSpec(match, rows),
        default_dimensions: dims,
      };
      return metric
        ? datasetMetricsApi.update(datasetId, metric.id, body)
        : datasetMetricsApi.create(datasetId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["datasets", datasetId, "metrics"] });
      onClose();
    },
  });

  const toggleDim = (n: string) =>
    setDims((d) => (d.includes(n) ? d.filter((x) => x !== n) : [...d, n]));

  const incomplete =
    !name.trim() || (needsField && !field) || rowsIncomplete(rows);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {metric ? t("metrics.editTitle", "Modifica metrica") : t("metrics.addTitle", "Nuova metrica")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t("metrics.name", "Nome")}</span>
            <Input
              value={name}
              autoFocus
              placeholder={t("metrics.namePlaceholder", "es. fatturato")}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-3">
            <label className="block flex-1 space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{t("metrics.aggregation", "Aggregazione")}</span>
              <select className={selectClass} value={agg} onChange={(e) => setAgg(e.target.value)}>
                {aggregations.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </label>
            {needsField && (
              <label className="block flex-1 space-y-1">
                <span className="text-xs font-medium text-muted-foreground">{t("metrics.field", "Campo")}</span>
                <select className={selectClass} value={field} onChange={(e) => setField(e.target.value)}>
                  <option value="">{t("metrics.pickField", "— scegli un campo —")}</option>
                  {/* keep an out-of-list field selectable (schema drift) */}
                  {field && !fields.some((f) => f.name === field) && (
                    <option value={field}>{field}</option>
                  )}
                  {fields.map((f) => (
                    <option key={f.name} value={f.name}>{f.name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t("metrics.description", "Descrizione")}</span>
            <Input
              value={description}
              placeholder={t("metrics.descriptionPlaceholder", "opzionale — cosa rappresenta")}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t("metrics.filter", "Filtro (opzionale)")}
            </span>
            <ConditionRows
              fields={fields}
              rows={rows}
              match={match}
              onRowsChange={setRows}
              onMatchChange={setMatch}
              emptyHint={t("metrics.filterNone", "Nessun filtro — la metrica considera tutte le righe.")}
            />
          </div>

          {fields.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t("metrics.dimensions", "Dimensioni suggerite (per …)")}
              </span>
              <div className="flex flex-wrap gap-2">
                {fields.map((f) => (
                  <label
                    key={f.name}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={dims.includes(f.name)}
                      onChange={() => toggleDim(f.name)}
                    />
                    {f.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {save.isError && (
            <Alert tone="danger" title={t("metrics.saveFailed", "Impossibile salvare")}>
              <span className="font-mono text-xs">{(save.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button disabled={save.isPending || incomplete} onClick={() => save.mutate()}>
            {t("common.save", "Salva")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
